// eslint-disable-next-line
import React, { useEffect, useState, useCallback, useRef } from "react";
import { MDBContainer } from "mdb-react-ui-kit";
import { useAuth } from "../context/AuthContext";
import "./VintedInboxPage.css";

// ───── Helper: translate a single string via our own backend proxy (avoids CORS) ─────
async function translateText(text, targetLang = "en") {
  if (!text || !text.trim()) return null;
  try {
    const res = await fetch(
      `https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/translate?text=${encodeURIComponent(
        text
      )}&target=${targetLang}`
    );
    const data = await res.json();
    if (data.success) return data.translated;
    return null;
  } catch (err) {
    console.warn("Translate failed for:", text, err);
    return null;
  }
}

// ───── Helper: parse raw conversation object into messages with sender ─────
// IMPORTANT: msg.id is now ALWAYS the real Vinted message id (msg.entity.id),
// never Math.random() — a random id would change on every re-fetch, which
// would break the "hide this message" feature (it needs a stable id to
// persist against on the backend).
function parseConversationMessages(rawConversation) {
  if (!rawConversation || !rawConversation.messages) return [];

  const oppositeUserId = rawConversation.opposite_user?.id;

  return rawConversation.messages
    .map((msg, idx) => {
      let body = "";
      let sender = "system";
      let type = msg.entity_type || "status_message";

      if (msg.entity_type === "message") {
        body = msg.entity.body;
        sender = (msg.entity.user_id === oppositeUserId) ? "buyer" : "seller";
      } else if (msg.entity_type === "status_message") {
        body = msg.entity.title;
        sender = "system";
      } else if (msg.entity_type === "offer_request_message") {
        body = `Offer: ${msg.entity.price_label} (${msg.entity.status_title})`;
        sender = (msg.entity.user_id === oppositeUserId) ? "buyer" : "seller";
      } else if (msg.entity_type === "offer_message") {
        body = `Counter offer: ${msg.entity.price_label}`;
        sender = (msg.entity.user_id === oppositeUserId) ? "buyer" : "seller";
      }

      // Stable id: prefer Vinted's own message id, fall back to array index
      // (never Math.random — must stay the same across re-fetches).
      const stableId = msg.entity?.id ?? msg.id ?? `idx-${idx}`;

      return {
        id: stableId,
        body,
        translated: null, // will be filled in after fetch
        sender,
        type,
        sent_at: msg.created_at_ts,
      };
    })
    .filter(msg => msg.body);   // empty messages hata do
}

// ───── Component ─────
const VintedInboxPage = () => {
  const { user, companyId } = useAuth();
  // eslint-disable-next-line
  const userId = user?.id || user?.user_id || user?.userId;

  const [conversations, setConversations] = useState([]);
  const [selectedConvId, setSelectedConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  // eslint-disable-next-line
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [vendorPhone, setVendorPhone] = useState("");

  // Seller dropdown — list of distinct sellers + currently selected one
  // ("" means "All Sellers", no filter applied).
  const [sellers, setSellers] = useState([]);
  const [selectedSeller, setSelectedSeller] = useState("");
  const [loadingSellers, setLoadingSellers] = useState(false);

  // Ids of messages the user has hidden for the CURRENTLY SELECTED conversation.
  // Kept as a Set of strings for O(1) lookup.
  const [hiddenIds, setHiddenIds] = useState(new Set());
  const [hidingId, setHidingId] = useState(null); // id currently being toggled (disables its button)

  // Simple in-memory cache so we don't re-translate the same string twice
  const translateCacheRef = useRef(new Map());

  // Sync userId with extension (existing logic)
  useEffect(() => {
    if (!companyId) return;
    fetch(
      "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/set-user-id",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: companyId }),
      }
    ).catch((err) => console.warn("UserId sync failed", err));
  }, [companyId]);

  // Fetch the distinct list of sellers for the dropdown
  const fetchSellers = useCallback(async () => {
    if (!companyId) return;
    setLoadingSellers(true);
    try {
      const res = await fetch(
        `https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/inbox/sellers?userId=${companyId}`
      );
      const data = await res.json();
      if (data.success) {
        setSellers(data.sellers);
      }
    } catch (err) {
      console.warn("Fetch sellers failed", err);
    } finally {
      setLoadingSellers(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchSellers();
  }, [fetchSellers]);

  // Fetch conversations, optionally filtered by the selected seller
  const fetchConversations = useCallback(async () => {
    if (!companyId) return;
    setLoadingConversations(true);
    try {
      const sellerParam = selectedSeller
        ? `&seller=${encodeURIComponent(selectedSeller)}`
        : "";
      const res = await fetch(
        `https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/inbox/conversations?userId=${companyId}${sellerParam}`
      );
      const data = await res.json();
      if (data.success) {
        setConversations(data.conversations);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingConversations(false);
    }
  }, [companyId, selectedSeller]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // If the currently selected conversation disappears after a seller filter
  // change (i.e. it belongs to a different seller), clear the messages panel.
  useEffect(() => {
    if (
      selectedConvId &&
      conversations.length > 0 &&
      !conversations.some((c) => c.conversation_id === selectedConvId)
    ) {
      setSelectedConvId(null);
      setMessages([]);
    }
  }, [conversations, selectedConvId]);

  // Translate all parsed messages in the background and update state as results come in
  const translateMessages = useCallback(async (parsedMessages) => {
    // Kick off translations sequentially (gentle on rate limits)
    for (const msg of parsedMessages) {
      const cache = translateCacheRef.current;
      let translated = cache.get(msg.body);

      if (translated === undefined) {
        translated = await translateText(msg.body, "en");
        cache.set(msg.body, translated);
      }

      // Only attach translation if it's meaningfully different from the original
      if (translated && translated.trim().toLowerCase() !== msg.body.trim().toLowerCase()) {
        setMessages((prev) =>
          prev.map((m) => (m.id === msg.id ? { ...m, translated } : m))
        );
      }

      // small delay to avoid hammering the endpoint
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 120));
    }
  }, []);

  // Fetch which message ids are hidden for this conversation
  const fetchHiddenIds = async (conversationId) => {
    try {
      const res = await fetch(
        `https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/hidden-messages/${conversationId}?userId=${companyId}`
      );
      const data = await res.json();
      if (data.success && Array.isArray(data.hiddenIds)) {
        setHiddenIds(new Set(data.hiddenIds.map(String)));
      } else {
        setHiddenIds(new Set());
      }
    } catch (err) {
      console.warn("Fetch hidden ids failed", err);
      setHiddenIds(new Set());
    }
  };

  // Toggle hide/unhide for one message. Optimistic UI update, then persist.
  const toggleHideMessage = async (messageId) => {
    const key = String(messageId);
    const currentlyHidden = hiddenIds.has(key);
    setHidingId(key);

    // optimistic update
    setHiddenIds((prev) => {
      const next = new Set(prev);
      if (currentlyHidden) next.delete(key);
      else next.add(key);
      return next;
    });

    try {
      const res = await fetch(
        "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/hidden-messages",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: companyId,
            conversationId: selectedConvId,
            messageId: key,
            hidden: !currentlyHidden,
          }),
        }
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.error || "Failed to save");
    } catch (err) {
      console.warn("Toggle hide failed, reverting", err);
      // revert optimistic update on failure
      setHiddenIds((prev) => {
        const next = new Set(prev);
        if (currentlyHidden) next.add(key);
        else next.delete(key);
        return next;
      });
      alert("Could not update hidden state: " + err.message);
    } finally {
      setHidingId(null);
    }
  };

  // Fetch messages by reading raw conversation JSON from server, then parse
  const fetchMessages = async (conversationId) => {
    setSelectedConvId(conversationId);
    setLoadingMessages(true);
    try {
      const [convRes] = await Promise.all([
        fetch(
          `https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/raw-conversation/${conversationId}?userId=${companyId}`
        ),
        fetchHiddenIds(conversationId), // runs in parallel, populates hiddenIds state
      ]);
      const data = await convRes.json();
      if (data.success && data.rawConversation) {
        const parsed = parseConversationMessages(data.rawConversation);
        setMessages(parsed);
        // fire-and-forget: translate in background, updates state as it goes
        translateMessages(parsed);
      } else {
        // fallback: empty
        setMessages([]);
      }
    } catch (err) {
      setError(err.message);
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSendToVendor = async () => {
    if (!selectedConvId) {
      alert("Please select a conversation first.");
      return;
    }
    const phone = vendorPhone.trim();
    if (!phone) {
      alert("Enter a valid WhatsApp number with country code.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(
        "https://clextensions-a6bvf6amfrc9aeex.centralindia-01.azurewebsites.net/api/send-whatsapp-command",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId: companyId,
            conversationId: selectedConvId,
            vendorPhone: phone,
          }),
        }
      );
      const result = await res.json();
      if (res.ok && result.success) {
        alert("✅ Command sent! Check WhatsApp Web – image will be attached.");
      } else {
        alert("❌ Failed: " + (result.error || "Unknown error"));
      }
    } catch (err) {
      alert("❌ Network error: " + err.message);
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    return new Date(timestamp).toLocaleString();
  };

  const getMessageAlignment = (msg) => {
    if (msg.sender === "system") return "center";
    return msg.sender === "seller" ? "right" : "left";
  };

  return (
    <MDBContainer fluid className="vinted-inbox-container">
      <h2 className="mb-4 d-flex align-items-center flex-wrap">
        <span>Vinted Inbox</span>

        {/* Seller filter dropdown */}
        <select
          className="form-select form-select-sm d-inline-block ms-3"
          style={{ width: "auto" }}
          value={selectedSeller}
          onChange={(e) => setSelectedSeller(e.target.value)}
          disabled={loadingSellers}
          title="Filter conversations by seller"
        >
          <option value="">All Sellers</option>
          {sellers.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>

        <button
          className="btn btn-sm btn-outline-primary ms-3"
          onClick={() => {
            fetchSellers();
            fetchConversations();
            if (selectedConvId) fetchMessages(selectedConvId);
          }}
        >
          🔄 Refresh
        </button>
      </h2>

      <div className="inbox-layout">
        {/* Conversation List */}
        <div className="conversation-list-panel">
          {loadingConversations ? (
            <p>Loading conversations...</p>
          ) : error ? (
            <p className="text-danger">{error}</p>
          ) : conversations.length === 0 ? (
            <p>
              {selectedSeller
                ? `No conversations found for "${selectedSeller}".`
                : "No conversations found."}
            </p>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.conversation_id}
                className={`conversation-item ${
                  selectedConvId === conv.conversation_id ? "active" : ""
                }`}
                onClick={() => fetchMessages(conv.conversation_id)}
              >
                <img
                  src={conv.item_image_url || "https://via.placeholder.com/50"}
                  alt={conv.item_title}
                  className="conv-item-image"
                />
                <div className="conv-item-details">
                  <div className="conv-item-title">
                    <strong>{conv.buyer_name}</strong>
                    {conv.item_title && (
                      <span className="conv-item-product"> – {conv.item_title}</span>
                    )}
                  </div>
                  {conv.seller_name && (
                    <div className="conv-item-seller" style={{ fontSize: "11px", opacity: 0.7 }}>
                      Seller: {conv.seller_name}
                    </div>
                  )}
                  <div className="conv-item-preview">{conv.last_message}</div>
                  <div className="conv-item-time">{formatTime(conv.last_updated)}</div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Messages Panel */}
        <div className="messages-panel">
          {selectedConvId ? (
            loadingMessages ? (
              <p>Loading messages...</p>
            ) : messages.length === 0 ? (
              <p>No messages yet.</p>
            ) : (
              <>
                {/* Sticky header with buyer info and WhatsApp send */}
                {(() => {
                  const conv = conversations.find(
                    (c) => c.conversation_id === selectedConvId
                  );
                  const buyer = conv?.buyer_name || "Unknown";
                  const title = conv?.item_title || "";
                  const img = conv?.item_image_url || "https://via.placeholder.com/50";
                  return (
                    <div className="conversation-header sticky-header">
                      <div className="header-left">
                        <img src={img} alt={title} className="header-item-image" />
                        <div className="header-details">
                          <div className="header-buyer-name">{buyer}</div>
                          {title && <div className="header-item-title">{title}</div>}
                        </div>
                      </div>
                      <div className="header-right">
                        <input
                          type="text"
                          className="vendor-phone-input"
                          placeholder="92xxxxxxxxxx"
                          value={vendorPhone}
                          onChange={(e) => setVendorPhone(e.target.value)}
                        />
                        <button
                          className="btn btn-sm btn-success ms-2"
                          onClick={handleSendToVendor}
                          disabled={sending}
                        >
                          {sending ? "Sending..." : "📤 Send to Vendor"}
                        </button>
                      </div>
                    </div>
                  );
                })()}

                {/* Messages container */}
                <div className="messages-container">
                  {messages.map((msg) => {
                    const isHidden = hiddenIds.has(String(msg.id));
                    return (
                      <div
                        key={msg.id}
                        className={`message-bubble ${getMessageAlignment(msg)} ${
                          msg.type === "status_message" ? "status-message" : ""
                        } ${msg.type === "offer_request_message" || msg.type === "offer_message" ? "offer-message" : ""} ${
                          isHidden ? "message-hidden" : ""
                        }`}
                        style={isHidden ? { opacity: 0.4 } : undefined}
                      >
                        <div className="message-content">
                          {/* Hide/unhide toggle — only makes sense for real buyer/seller
                              text messages; hiding a message excludes it from the
                              WhatsApp caption sent to the vendor. */}
                          {msg.type === "message" && (
                            <button
                              type="button"
                              className="message-hide-toggle"
                              title={isHidden ? "Unhide this message" : "Hide this message (won't be sent to vendor)"}
                              disabled={hidingId === String(msg.id)}
                              onClick={() => toggleHideMessage(msg.id)}
                              style={{
                                float: msg.sender === "seller" ? "left" : "right",
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                fontSize: "13px",
                                opacity: 0.7,
                                marginLeft: "6px",
                                marginRight: "6px",
                              }}
                            >
                              {isHidden ? "🙈" : "👁️"}
                            </button>
                          )}
                          <div className="message-text">
                            {isHidden && <span style={{ fontSize: "11px", fontStyle: "italic" }}>(hidden) </span>}
                            {msg.body}
                          </div>
                          {msg.translated && (
                            <div
                              className="message-translation"
                              style={{
                                color: "#0a7cff",
                                fontSize: "12px",
                                fontStyle: "italic",
                                marginTop: "2px",
                              }}
                            >
                              → {msg.translated}
                            </div>
                          )}
                          <div className="message-time">{formatTime(msg.sent_at)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )
          ) : (
            <p>Select a conversation to view messages</p>
          )}
        </div>
      </div>
    </MDBContainer>
  );
};

export default VintedInboxPage;
